import { useCallback, useEffect, useRef, useState } from "react";
import type { Quill } from "react-quill-new";
import { applyImageScale, parseScaleInput, readImageScale } from "./imageScale";

export type GetEditor = () => Quill | null;

export interface ImageScaleAnchor {
    x: number;
    y: number;
}

const PANEL_SELECTOR = "[data-editor-image-scale]";

const imageFromPointer = (event: MouseEvent, root: HTMLElement): HTMLImageElement | null => {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    for (let i = 0; i < path.length; i += 1) {
        const node = path[i];
        if (node instanceof HTMLImageElement && root.contains(node)) return node;
    }

    const target = event.target;
    const fromTarget = target instanceof Element ? target.closest("img") : null;
    if (fromTarget instanceof HTMLImageElement && root.contains(fromTarget)) return fromTarget;

    if (typeof document.elementsFromPoint !== "function") return null;
    const stack = document.elementsFromPoint(event.clientX, event.clientY);
    for (let i = 0; i < stack.length; i += 1) {
        const el = stack[i];
        if (el instanceof HTMLImageElement && root.contains(el)) return el;
    }
    return null;
};

const scaleDraft = (img: HTMLImageElement): { ready: boolean; draft: string } => {
    const loaded = Number.isFinite(img.naturalWidth) && img.naturalWidth >= 1;
    const scale = readImageScale(img);
    if (scale != null) return { ready: true, draft: String(scale) };
    if (loaded && !img.getAttribute("width")) return { ready: true, draft: "100" };
    return { ready: loaded, draft: "" };
};

/** The picture node can be replaced when Quill rebuilds the editor. */
const resolveImage = (quill: Quill, img: HTMLImageElement): HTMLImageElement | null => {
    if (quill.root.contains(img)) return img;
    const src = img.getAttribute("src");
    if (!src) return null;
    const nodes = quill.root.querySelectorAll("img");
    for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes.item(i);
        if (node?.getAttribute("src") === src) return node;
    }
    return null;
};

const eventInPanel = (event: MouseEvent): boolean => {
    const target = event.target;
    const el = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    return Boolean(el?.closest(PANEL_SELECTOR));
};

/**
 * Click on a picture opens a small panel next to the cursor.
 * The document changes only when the user saves a percent from 10 to 200.
 * The handler always asks for the live editor: react-quill rebuilds Quill and the
 * previous root no longer contains the pictures.
 */
const useImageScaleField = (getQuill: GetEditor, enabled: boolean) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [anchor, setAnchor] = useState<ImageScaleAnchor | null>(null);
    const [draft, setDraft] = useState("");
    const [ready, setReady] = useState(false);
    const getQuillRef = useRef(getQuill);
    const openRef = useRef(false);
    getQuillRef.current = getQuill;
    openRef.current = image != null;

    const close = useCallback(() => {
        setImage(null);
        setAnchor(null);
        setDraft("");
        setReady(false);
    }, []);

    const save = useCallback(() => {
        const quill = getQuillRef.current();
        const current = image && quill ? resolveImage(quill, image) : null;
        if (!quill || !current) {
            close();
            return;
        }
        const parsed = parseScaleInput(draft);
        if (parsed == null) return;
        applyImageScale(quill, current, parsed);
        close();
    }, [close, draft, image]);

    useEffect(() => {
        if (!enabled) return;

        const onPointerDown = (event: MouseEvent) => {
            if (eventInPanel(event)) return;
            const quill = getQuillRef.current();
            const root = quill?.root;
            if (!root) return;
            const img = imageFromPointer(event, root);
            if (!img) {
                if (openRef.current) close();
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            const next = scaleDraft(img);
            setImage(img);
            setAnchor({ x: event.clientX, y: event.clientY });
            setReady(next.ready);
            setDraft(next.draft);
        };

        const onPointerUp = (event: MouseEvent) => {
            if (!eventInPanel(event)) return;
            event.stopPropagation();
        };

        document.addEventListener("mousedown", onPointerDown, true);
        document.addEventListener("mouseup", onPointerUp, true);
        return () => {
            document.removeEventListener("mousedown", onPointerDown, true);
            document.removeEventListener("mouseup", onPointerUp, true);
        };
    }, [close, enabled]);

    useEffect(() => {
        if (!enabled || !image) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [close, enabled, image]);

    useEffect(() => {
        if (!image) return;
        const onLoad = () => {
            const next = scaleDraft(image);
            setReady(next.ready);
            setDraft((prev) => (document.activeElement instanceof HTMLInputElement ? prev : next.draft));
        };
        if (image.complete && image.naturalWidth >= 1) onLoad();
        else image.addEventListener("load", onLoad);
        return () => image.removeEventListener("load", onLoad);
    }, [image]);

    return {
        open: image != null,
        anchor,
        draft,
        setDraft,
        ready,
        canSave: ready && parseScaleInput(draft) != null,
        save,
        close,
    };
};

export default useImageScaleField;
